import InlineTextButton from '@ben/orion/inline-text-button';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { ModularBenefitAudience } from '@ben/shared/utils/modular-benefit-audience-visibility';
import type { RestrictionsByType } from '../../../types/columbia';
import type {
  IEmployeeBenefit,
  IModularBenefitContact,
} from '../../../types/modular-benefit';
import type { AcceptedContact } from '@ben/shared/utils/contacts';
import { isNil } from 'lodash';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import ConditionalApprovalInfo from '../conditional-approval-info';
import ConfigWindowClosedBanner from '../config-window-closed-banner';
import { useNavigateProfileDependents } from '../use-navigate-profile-dependents/use-navigate-profile-dependents';
import WindowInfo from '../window-info';
import ContactCard from './contact-card';
import ContactSelect from './contact-select';
import ContactSelectionInfo from './contacts-selection-info';
import {
  useContactSelection,
  type ContactSelection,
} from './use-contact-selection';

export interface ContactsProps {
  title?: string;
  showTitle?: boolean;
  componentKey: string;
  value: ContactSelection[];
  contacts: IModularBenefitContact[];
  onChange: (value: ContactSelection[]) => void;
  restrictionsByType: RestrictionsByType;
  configWindow?: {
    isOpen?: boolean;
    startTimestamp?: string;
    endTimestamp?: string;
  };
  benefit?: IEmployeeBenefit;
  max?: number;
  min?: number;
  isConditionalApprovalTriggered?: boolean;
  acceptedContactTypes?: AcceptedContact[];
  originalUserSelection?: Record<string, unknown>;
  hasActiveWindowExclusion?: boolean;
  backendErrors?: Record<string, unknown>[];
  audience?: ModularBenefitAudience;
}

const Contacts = ({
  title,
  showTitle,
  onChange,
  max,
  min,
  restrictionsByType,
  value,
  contacts,
  configWindow,
  benefit,
  componentKey,
  isConditionalApprovalTriggered,
  acceptedContactTypes,
  originalUserSelection,
  hasActiveWindowExclusion,
  backendErrors,
  audience = 'employee',
}: ContactsProps) => {
  const { t } = useTranslation();

  const {
    selectInstanceId,
    selectedContactIds,
    handleContactSelectChange,
    isConfigWindowClosed,
    isConfigWindowOpen,
    selectedContactDetails,
    contactSubComponentValues,
    handleContactSubComponentValueChange,
    handleContactRemove,
    requiredContactFields,
  } = useContactSelection({
    value,
    contacts,
    configWindow,
    benefit,
    onChange,
    hasActiveWindowExclusion,
  });

  const { navigateAddNewDependent } = useNavigateProfileDependents({
    requiredContactFields,
    acceptedContactTypes,
  });

  const storedUserSelection = originalUserSelection
    ? originalUserSelection[componentKey]
    : benefit?.enrolment?.additionalInfo?.userInput?.[componentKey];

  return (
    <Container>
      {isConfigWindowClosed && <ConfigWindowClosedBanner />}
      <TitleWrapper>
        {title && showTitle && (
          <Typography variant={TypographyVariant.h3}>{title}</Typography>
        )}
        <Typography variant={TypographyVariant.bodyMd1}>
          {t('contacts.onlyEligible')}
        </Typography>
      </TitleWrapper>
      <ContactSelect
        key={selectInstanceId}
        label={t('contacts.selectContact')}
        contacts={contacts}
        selectedContactIds={selectedContactIds}
        onChange={handleContactSelectChange}
        disabled={
          (max && selectedContactIds.length >= max) || isConfigWindowClosed
        }
        restrictionsByType={restrictionsByType}
      />

      {!isConfigWindowOpen && !isNil(configWindow) && (
        <WindowInfo
          startDate={configWindow.startTimestamp ?? ''}
          endDate={configWindow.endTimestamp ?? ''}
        />
      )}
      {isConditionalApprovalTriggered && (
        <StyledWindowInfo>
          <ConditionalApprovalInfo />
        </StyledWindowInfo>
      )}
      <ContactSelectionInfo
        max={max}
        min={min}
        restrictionsByType={restrictionsByType}
        acceptedContactTypes={acceptedContactTypes}
      />
      {selectedContactDetails?.map((contact) => {
        const index = value.findIndex(
          (value: ContactSelection) => String(value.id) === contact.key
        );
        const contactBackendErrors =
          index !== -1 && backendErrors && Array.isArray(backendErrors)
            ? backendErrors[index]
            : undefined;
        return (
          <ContactCard
            disabled={isConfigWindowClosed}
            key={contact.key}
            contact={contact}
            onRemove={handleContactRemove}
            onChildValueChange={handleContactSubComponentValueChange}
            benefitId={Number(benefit?.benefit?.id)}
            contactSubComponentValues={contactSubComponentValues}
            originalComponentValues={storedUserSelection}
            requiredContactFields={requiredContactFields}
            backendErrors={contactBackendErrors}
            audience={audience}
          />
        );
      })}
      {!isConfigWindowClosed && (
        <ButtonsWrapper>
          <InlineTextButton
            label={t('contacts.createDependent')}
            onClick={navigateAddNewDependent}
            disabled={isConfigWindowClosed}
            isUnderlined
          />
        </ButtonsWrapper>
      )}
    </Container>
  );
};

export default memo(Contacts);

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `}
`;

const ButtonsWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: flex-start;
    gap: ${theme.spacing(2)};
  `}
`;

const TitleWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;

const StyledWindowInfo = styled.div`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacing(2)};
  `}
`;
